import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, CircuitBoard, RotateCcw, Star, Target, Info, Zap, Check, X, Trash2, Play } from 'lucide-react';
import { useLabProgress } from '../../hooks/useLabProgress';


const GRID = 10;
const CELL = 55;

const COMPONENTS = [
  { id: 'battery', label: 'Battery', symbol: '🔋', resistance: 0, voltage: 5, color: '#22c55e' },
  { id: 'bulb', label: 'Bulb', symbol: '💡', resistance: 100, voltage: 0, color: '#facc15' },
  { id: 'resistor', label: 'Resistor', symbol: '⚡', resistance: 50, voltage: 0, color: '#f97316' },
  { id: 'switch', label: 'Switch', symbol: '🔘', resistance: 0, voltage: 0, color: '#60a5fa' },
  { id: 'wire', label: 'Wire', symbol: '—', resistance: 0, voltage: 0, color: '#64748b' },
];

const MISSIONS = [
  { id: 1, name: 'Simple Circuit', description: 'Connect a battery to a bulb with wires', required: ['battery', 'bulb', 'wire'], stars: 1 },
  { id: 2, name: 'Series Circuit', description: 'Connect 2 bulbs in series with a battery', required: ['battery', 'bulb', 'bulb', 'wire'], stars: 2 },
  { id: 3, name: 'With Resistor', description: 'Add a resistor to protect the bulb', required: ['battery', 'resistor', 'bulb', 'wire'], stars: 2 },
  { id: 4, name: 'Switch Control', description: 'Add a switch to control the circuit', required: ['battery', 'switch', 'bulb', 'wire'], stars: 3 },
  { id: 5, name: 'Parallel Bulbs', description: 'Connect 2 bulbs in parallel to a battery', required: ['battery', 'bulb', 'bulb', 'wire', 'wire'], stars: 2 },
  { id: 6, name: 'Dual Resistors', description: 'Connect 2 resistors in series with a bulb', required: ['battery', 'resistor', 'resistor', 'bulb', 'wire'], stars: 2 },
  { id: 7, name: 'Three Bulb Chain', description: 'Wire 3 bulbs in series — observe brightness', required: ['battery', 'bulb', 'bulb', 'bulb', 'wire'], stars: 2 },
  { id: 8, name: 'Protected Switch', description: 'Build a switched circuit with a resistor', required: ['battery', 'switch', 'resistor', 'bulb', 'wire'], stars: 3 },
  { id: 9, name: 'Complex Series', description: 'Battery + switch + 2 resistors + 2 bulbs in series', required: ['battery', 'switch', 'resistor', 'resistor', 'bulb', 'bulb', 'wire'], stars: 3 },
  { id: 10, name: 'Master Circuit', description: 'Use all component types in one circuit', required: ['battery', 'switch', 'resistor', 'bulb', 'bulb', 'wire', 'wire'], stars: 3 },
];

export default function ElectricalLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('electrical');
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('electrical', 10);
  const [placedComponents, setPlacedComponents] = useState([]);
  const [selectedTool, setSelectedTool] = useState(null);
  const [isCircuitComplete, setIsCircuitComplete] = useState(false);
  const [isPowered, setIsPowered] = useState(false);
  const [circuitInfo, setCircuitInfo] = useState(null);
  const [result, setResult] = useState(null);

  const mission = MISSIONS[currentMission];

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Background
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, w, h);

    // Grid
    for (let x = 0; x <= GRID; x++) {
      for (let y = 0; y <= GRID; y++) {
        ctx.beginPath();
        ctx.arc(x * CELL + CELL / 2, y * CELL + CELL / 2, 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        ctx.fill();
      }
    }

    // Draw placed components
    placedComponents.forEach((comp, idx) => {
      const x = comp.gridX * CELL + CELL / 2;
      const y = comp.gridY * CELL + CELL / 2;
      const compDef = COMPONENTS.find(c => c.id === comp.type);
      if (!compDef) return;

      // Component background
      ctx.beginPath();
      ctx.roundRect(x - 22, y - 22, 44, 44, 8);
      ctx.fillStyle = isPowered && isCircuitComplete ? compDef.color + '30' : 'rgba(30,41,59,0.8)';
      ctx.fill();
      ctx.strokeStyle = isPowered && isCircuitComplete ? compDef.color : 'rgba(148,163,184,0.3)';
      ctx.lineWidth = isPowered && isCircuitComplete ? 2 : 1;
      ctx.stroke();

      // Symbol
      ctx.font = '22px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(compDef.symbol, x, y);

      // Glow for powered bulb
      if (comp.type === 'bulb' && isPowered && isCircuitComplete) {
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 30);
        glow.addColorStop(0, 'rgba(250,204,21,0.3)');
        glow.addColorStop(1, 'rgba(250,204,21,0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, 30, 0, Math.PI * 2);
        ctx.fill();
      }

      // Label
      ctx.fillStyle = '#94a3b8';
      ctx.font = '9px sans-serif';
      ctx.fillText(compDef.label, x, y + 32);
    });

    // Draw wires between adjacent components
    for (let i = 0; i < placedComponents.length; i++) {
      for (let j = i + 1; j < placedComponents.length; j++) {
        const a = placedComponents[i];
        const b = placedComponents[j];
        const dx = Math.abs(a.gridX - b.gridX);
        const dy = Math.abs(a.gridY - b.gridY);
        if ((dx === 1 && dy === 0) || (dx === 0 && dy === 1)) {
          const ax = a.gridX * CELL + CELL / 2;
          const ay = a.gridY * CELL + CELL / 2;
          const bx = b.gridX * CELL + CELL / 2;
          const by = b.gridY * CELL + CELL / 2;
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(bx, by);
          ctx.strokeStyle = isPowered && isCircuitComplete ? '#22c55e80' : '#64748b40';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    }

    // Current flow animation dots
    if (isPowered && isCircuitComplete) {
      const t = Date.now() / 300;
      for (let i = 0; i < placedComponents.length; i++) {
        for (let j = i + 1; j < placedComponents.length; j++) {
          const a = placedComponents[i];
          const b = placedComponents[j];
          const dx = Math.abs(a.gridX - b.gridX);
          const dy = Math.abs(a.gridY - b.gridY);
          if ((dx === 1 && dy === 0) || (dx === 0 && dy === 1)) {
            const ax = a.gridX * CELL + CELL / 2;
            const ay = a.gridY * CELL + CELL / 2;
            const bx = b.gridX * CELL + CELL / 2;
            const by = b.gridY * CELL + CELL / 2;
            const progress = (t + i * 0.3) % 1;
            const dotX = ax + (bx - ax) * progress;
            const dotY = ay + (by - ay) * progress;
            ctx.beginPath();
            ctx.arc(dotX, dotY, 3, 0, Math.PI * 2);
            ctx.fillStyle = '#22c55e';
            ctx.fill();
          }
        }
      }
    }
  }, [placedComponents, isPowered, isCircuitComplete]);

  useEffect(() => { draw(); }, [draw]);

  // Animation loop for current flow
  useEffect(() => {
    if (isPowered && isCircuitComplete) {
      let running = true;
      const anim = () => {
        if (!running) return;
        draw();
        requestAnimationFrame(anim);
      };
      requestAnimationFrame(anim);
      return () => { running = false; };
    }
  }, [isPowered, isCircuitComplete, draw]);

  const handleCanvasClick = (e) => {
    if (!selectedTool) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;

    const gridX = Math.floor(mx / CELL);
    const gridY = Math.floor(my / CELL);

    if (gridX < 0 || gridX >= GRID || gridY < 0 || gridY >= GRID) return;

    // Check if cell is occupied
    const existing = placedComponents.findIndex(c => c.gridX === gridX && c.gridY === gridY);
    if (existing >= 0) {
      // Remove
      setPlacedComponents(prev => prev.filter((_, i) => i !== existing));
    } else {
      setPlacedComponents(prev => [...prev, { type: selectedTool, gridX, gridY }]);
    }
    setIsPowered(false);
    setResult(null);
    setCircuitInfo(null);
  };

  const checkCircuit = () => {
    // Count components
    const counts = {};
    placedComponents.forEach(c => {
      counts[c.type] = (counts[c.type] || 0) + 1;
    });

    const hasBattery = counts.battery >= 1;
    const hasBulb = counts.bulb >= 1;
    const hasWire = counts.wire >= 1;

    // Check adjacency (simplified - at least each component has a neighbor)
    let connected = true;
    placedComponents.forEach(comp => {
      const hasNeighbor = placedComponents.some(other =>
        other !== comp &&
        ((Math.abs(comp.gridX - other.gridX) === 1 && comp.gridY === other.gridY) ||
         (Math.abs(comp.gridY - other.gridY) === 1 && comp.gridX === other.gridX))
      );
      if (!hasNeighbor) connected = false;
    });

    const complete = hasBattery && hasBulb && connected && placedComponents.length >= 3;
    setIsCircuitComplete(complete);

    if (complete) {
      setIsPowered(true);
      const totalR = placedComponents.reduce((s, c) => s + (COMPONENTS.find(x => x.id === c.type)?.resistance || 0), 0) || 1;
      const v = placedComponents.find(c => c.type === 'battery') ? 5 : 0;
      const current = (v / totalR * 1000).toFixed(1);
      setCircuitInfo({ voltage: v, resistance: totalR, current });

      // Check mission requirements
      const reqCounts = {};
      mission.required.forEach(r => { reqCounts[r] = (reqCounts[r] || 0) + 1; });
      const missionComplete = Object.entries(reqCounts).every(([type, count]) => (counts[type] || 0) >= count);
      if (missionComplete) {
        setResult('correct');
        if (!completedMissions.includes(currentMission)) {
          saveProgress(currentMission);
        }
      } else {
        setResult('partial');
      }
    } else {
      setIsPowered(false);
      setResult('incomplete');
      setCircuitInfo(null);
    }
  };

  const clearCircuit = () => {
    setPlacedComponents([]);
    setIsCircuitComplete(false);
    setIsPowered(false);
    setResult(null);
    setCircuitInfo(null);
    // Force immediate canvas clear
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let x = 0; x <= GRID; x++) {
        for (let y = 0; y <= GRID; y++) {
          ctx.beginPath();
          ctx.arc(x * CELL + CELL / 2, y * CELL + CELL / 2, 2, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255,255,255,0.08)';
          ctx.fill();
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="electrical-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Electrical lab about circuits. Build circuits by connecting batteries, bulbs, and resistors. Learn Ohms Law visually."
        cacheKey="lab-intro-electrical"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <CircuitBoard className="w-5 h-5 text-yellow-400" />
              <span className="font-semibold">Electrical Circuits Lab</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MISSIONS.map((m, i) => (
              <Badge key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => { clearCircuit(); setCurrentMission(i); }} data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) && <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />}
                M{m.id}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <motion.div key={currentMission} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
          <Target className="w-5 h-5 text-yellow-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span>
          </div>
          <div className="ml-auto flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden border border-border/50 bg-card">
              <canvas ref={canvasRef} width={GRID * CELL} height={GRID * CELL} className="w-full cursor-pointer"
                onClick={handleCanvasClick} data-testid="circuit-canvas"
              />
            </div>

            <div className="flex items-center gap-3 mt-4">
              <Button onClick={checkCircuit} disabled={placedComponents.length === 0} data-testid="power-circuit-btn">
                <Play className="w-4 h-4 mr-2" /> Power On
              </Button>
              <Button variant="outline" onClick={clearCircuit} data-testid="clear-circuit-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Clear
              </Button>

              <AnimatePresence>
                {result === 'correct' && (
                  <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/10 text-green-400 border border-green-500/30 text-sm font-medium">
                    <Check className="w-4 h-4" /> Circuit complete! Bulb is glowing!
                  </motion.div>
                )}
                {result === 'incomplete' && (
                  <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 text-sm font-medium">
                    <X className="w-4 h-4" /> Circuit incomplete. Connect all components!
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="space-y-4">
            {/* Component palette */}
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400" /> Components
              </h4>
              <div className="space-y-2">
                {COMPONENTS.map(comp => (
                  <button key={comp.id} onClick={() => setSelectedTool(selectedTool === comp.id ? null : comp.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors text-left ${
                      selectedTool === comp.id ? 'border-primary bg-primary/10' : 'border-border/50 hover:bg-muted/50'
                    }`}
                    data-testid={`tool-${comp.id}`}
                  >
                    <span className="text-lg">{comp.symbol}</span>
                    <div>
                      <span className="text-xs font-semibold">{comp.label}</span>
                      {comp.resistance > 0 && <span className="text-[10px] text-muted-foreground ml-1">{comp.resistance}R</span>}
                      {comp.voltage > 0 && <span className="text-[10px] text-muted-foreground ml-1">{comp.voltage}V</span>}
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">Select a component, then click the grid to place it. Click an existing component to remove it.</p>
            </div>

            {/* Circuit info */}
            {circuitInfo && (
              <div className="p-4 rounded-xl bg-card border border-border/50">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-green-400" /> Circuit Data
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Voltage</span><span className="font-mono">{circuitInfo.voltage}V</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Resistance</span><span className="font-mono">{circuitInfo.resistance}R</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Current</span><span className="font-mono">{circuitInfo.current}mA</span></div>
                </div>
              </div>
            )}

            <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
              <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
                <Info className="w-3 h-3" /> Ohm's Law
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong>V = I x R</strong> — Voltage equals Current times Resistance.
                A complete circuit needs a power source (battery), a load (bulb), and a
                conducting path (wires). Components must be adjacent (touching) to connect.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
